import { useCallback } from 'react';

import { useToggleCareerLessonBookmarkMutation } from 'core/graphql';

export interface ISetCareerLessonBookmarkResult {
  success: boolean;
  /** The persisted flag echoed by the server (falls back to the desired value). */
  isBookmarked: boolean;
}

/**
 * Toggles the ☆/★ bookmark on an unlocked career lesson (spec 0020 Фаза C).
 *
 * The desired state is passed explicitly, so a double-tap is idempotent. The
 * button reflects the returned `isBookmarked`; the list's ★ refreshes on its
 * next `cache-and-network` read (the payload returns only the flag, not the
 * lesson entity, so there is no cache entity to auto-patch).
 *
 * A network failure propagates as a thrown ApolloError — the button reverts its
 * optimistic state and stays interactive.
 */
export const useSetCareerLessonBookmark = () => {
  const [mutate, { loading }] = useToggleCareerLessonBookmarkMutation();

  const setBookmark = useCallback(
    async (lessonId: string, isBookmarked: boolean): Promise<ISetCareerLessonBookmarkResult> => {
      const { data } = await mutate({ variables: { lessonId, isBookmarked } });
      const payload = data?.setCareerLessonBookmark;

      return {
        success: Boolean(payload?.success),
        isBookmarked: payload?.isBookmarked ?? isBookmarked,
      };
    },
    [mutate],
  );

  return { setBookmark, loading };
};
